import { useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import {
  EditOutlined,
  InboxOutlined,
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  RedoOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  StopOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserAddOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayVN, soVN } from '../../../lib/dinh-dang';
import { useAuthStore } from '../../../stores/auth';
import { useMfaCaToChuc } from '../../bao-mat/hooks/useBaoMat';
import { useCayPhongBan } from '../../co-cau-to-chuc/hooks/usePhongBan';
import { tenDayDu } from '../../co-cau-to-chuc/nghiep-vu/cay';
import { useVaiTroCuaNhieuNguoi } from '../../vai-tro/hooks/useVaiTroNguoiDung';
import {
  BADGE_BAT_BUOC_MFA,
  BADGE_TAM_KHOA_DANG_NHAP,
  BADGE_QUAN_TRI_TO_CHUC,
  TRANG_THAI_NGUOI_DUNG,
  trangThaiCaiMfa,
} from '../constants';
import { useDanhSachNguoiDung } from '../hooks/useNguoiDung';
import { useThaoTacNguoiDung } from '../hooks/useThaoTacNguoiDung';
import {
  dangTamKhoaDangNhap,
  datLaiMfaDuoc,
  doiEmailNhanThuDuoc,
  emailNhanThuRieng,
  ganVaiTroDuoc,
  guiDatLaiMatKhauDuoc,
  guiLaiLoiMoiDuoc,
  khoaDuoc,
  moKhoaDuoc,
} from '../nghiep-vu/thao-tac';
import type { UserProfile, UserStatus } from '../types';

const THU_TU_TRANG_THAI: UserStatus[] = ['ACTIVE', 'INVITED', 'DISABLED'];

/**
 * Cấu hình người dùng của tổ chức (cổng tenant, `/he-thong/nguoi-dung`): danh
 * sách, mời, sửa tên, khoá và mở khoá, gửi lại lời mời, lấy lại tài khoản
 * (email đặt lại mật khẩu, mở khoá đăng nhập, đặt lại xác thực hai lớp). Chỉ quản trị tổ chức
 * vào được — nhánh `/he-thong` thuộc dịch vụ `cau-hinh-he-thong`
 * (`DICH_VU_CHI_QUAN_TRI`), phân hệ `USER_CONFIG`.
 *
 * Lọc trạng thái ở server (`status`) và phân trang theo cursor bằng nút "Tải
 * thêm": tenant khoảng 30 người (ADR-0013), một hai trang là hết.
 */
export function CauHinhNguoiDungView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toiLa = useAuthStore((s) => s.user?.id);
  const [trangThai, setTrangThai] = useState<UserStatus | undefined>();
  // Chỉ để đổi `department_id` thành tên; lỗi thì cột lùi về "Không rõ", không chặn bảng.
  const cayPhongBan = useCayPhongBan();
  const dsPhongBan = useMemo(() => cayPhongBan.data ?? [], [cayPhongBan.data]);
  const q = useDanhSachNguoiDung(trangThai);
  const thaoTac = useThaoTacNguoiDung();
  // Vai trò lớp 2 chỉ đọc cho người không phải Quản trị tổ chức — quản trị đã toàn quyền.
  const idCanVaiTro = useMemo(
    () => q.dong.filter((u) => u.platform_role !== 'TENANT_ADMIN').map((u) => u.user_id),
    [q.dong],
  );
  const vaiTroTheoNguoi = useVaiTroCuaNhieuNguoi(idCanVaiTro);
  // Mức xác thực hai lớp của tổ chức: `undefined` khi chưa đọc được chính sách —
  // lúc đó cột chỉ dựa vào cờ riêng và mục bật/bỏ trong menu ẩn đi (ADR-UI-0005).
  const toChucBatBuocMfa = useMfaCaToChuc();
  const tieuDe = t('nav.muc.cauHinhNguoiDung');

  const coLoc = Boolean(trangThai);
  // Lỗi khi đã có dữ liệu là lỗi của lần "Tải thêm": giữ bảng, báo cạnh nút.
  const loiTaiDau = q.error && q.dong.length === 0 && !q.isFetchNextPageError;
  const dangTaiDau = q.isLoading;

  const columns: TableProps<UserProfile>['columns'] = [
    {
      title: t('nguoiDung.cotNguoiDung'),
      width: 260,
      render: (_v, row) => {
        // ADR-0101: chỉ nói khi thư đi tới hộp khác tên đăng nhập.
        const nhanThuRieng = emailNhanThuRieng(row);
        return (
          <div className="flex min-w-0 max-w-64 flex-col">
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="truncate text-sm font-medium text-content-primary">{row.display_name}</span>
              {row.user_id === toiLa && (
                <span className="shrink-0 text-2xs text-content-muted">{t('nguoiDung.ban')}</span>
              )}
            </span>
            <span className="truncate text-2xs text-content-muted">
              {row.email ?? t('nguoiDung.chuaDocDuocEmail')}
            </span>
            {nhanThuRieng && (
              <span className="truncate text-2xs text-content-muted">
                {t('nguoiDung.nhanThu', { email: nhanThuRieng })}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: t('nguoiDung.cotVaiTro'),
      width: 200,
      render: (_v, row) => {
        if (row.platform_role === 'TENANT_ADMIN') {
          return <Badge variant={BADGE_QUAN_TRI_TO_CHUC.variant}>{t(BADGE_QUAN_TRI_TO_CHUC.khoa)}</Badge>;
        }
        const vt = vaiTroTheoNguoi.get(row.user_id);
        if (!vt || vt.dangTai) return <span className="text-xs text-content-muted">{KHONG_CO}</span>;
        if (vt.loi) return <span className="text-xs text-content-muted">{t('nguoiDung.vaiTroKhongRo')}</span>;
        if (vt.ten.length === 0) {
          return <span className="text-xs text-content-muted">{t('nguoiDung.chuaGanVaiTro')}</span>;
        }
        const ds = vt.ten.join(', ');
        return (
          <span className="block max-w-48 truncate text-sm text-content-primary" title={ds}>
            {ds}
          </span>
        );
      },
    },
    {
      title: t('nguoiDung.cotPhongBan'),
      dataIndex: 'department_id',
      width: 200,
      render: (value: string | null) => {
        if (value === null) return <span className="text-xs text-content-muted">{t('nguoiDung.chuaCoPhongBan')}</span>;
        const phongBan = dsPhongBan.find((d) => d.id === value);
        if (!phongBan) {
          return (
            <span className="text-xs text-content-muted">
              {cayPhongBan.isLoading ? KHONG_CO : t('nguoiDung.phongBanKhongRo')}
            </span>
          );
        }
        // Ô chỉ hiện phòng ban của người đó; tên đầy đủ từ gốc ở tooltip.
        return (
          <span className="block max-w-48 truncate text-sm text-content-primary" title={tenDayDu(dsPhongBan, value)}>
            {phongBan.name}
          </span>
        );
      },
    },
    {
      title: t('chung.trangThai'),
      dataIndex: 'status',
      width: 160,
      render: (value: UserStatus, row) => {
        const cauHinh = TRANG_THAI_NGUOI_DUNG[value];
        const trangThai = cauHinh ? <Badge variant={cauHinh.variant}>{t(cauHinh.khoa)}</Badge> : value;
        if (!dangTamKhoaDangNhap(row)) return trangThai;
        return (
          <div className="flex flex-col items-start gap-1">
            {trangThai}
            <Badge variant={BADGE_TAM_KHOA_DANG_NHAP.variant}>{t(BADGE_TAM_KHOA_DANG_NHAP.khoa)}</Badge>
          </div>
        );
      },
    },
    {
      title: t('nguoiDung.mfa.cot'),
      width: 140,
      render: (_v, row) => {
        const batBuoc = toChucBatBuocMfa === true || row.mfa_required;
        const cai = trangThaiCaiMfa(row.mfa_configured, batBuoc);
        // Chữ trơn lùi `px-2` như chữ trong Badge: dòng cài đặt thẳng mép với
        // badge "Bắt buộc" ở trên, và với ô một dòng của những người khác.
        const dongCai = <span className={`px-2 text-xs ${cai.lop}`}>{t(cai.khoa)}</span>;
        if (!batBuoc) return dongCai;
        return (
          <div className="flex flex-col items-start gap-1">
            <Badge variant={BADGE_BAT_BUOC_MFA.variant}>{t(BADGE_BAT_BUOC_MFA.khoa)}</Badge>
            {dongCai}
          </div>
        );
      },
    },
    {
      title: t('nguoiDung.cotCapNhat'),
      dataIndex: 'updated_at',
      width: 120,
      render: (value: string) => (
        <span className="whitespace-nowrap text-xs text-content-muted">{ngayVN(value)}</span>
      ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) => (
        <MenuThaoTac
          tenBanGhi={row.display_name}
          muc={[
            {
              khoa: 'sua',
              nhan: t('nguoiDung.suaThongTin'),
              icon: <EditOutlined />,
              onChon: () => thaoTac.moSua(row),
            },
            {
              khoa: 'gan-vai-tro',
              nhan: t('nguoiDung.ganVaiTro'),
              icon: <SafetyCertificateOutlined />,
              // Không cho chính mình (ADR-0004), không cho Quản trị tổ chức — đã toàn quyền (ADR-0077).
              hien: ganVaiTroDuoc(row, toiLa),
              onChon: () => thaoTac.moGanVaiTro(row),
            },
            {
              khoa: 'bat-mfa',
              nhan: t('nguoiDung.mfa.batBuocMuc'),
              icon: <SafetyOutlined />,
              // Tổ chức bắt buộc cả rồi (hay chưa biết mức) thì cờ riêng không đổi gì: ẩn.
              // Bản ghi mồ côi không còn tài khoản Keycloak để buộc OTP.
              hien: toChucBatBuocMfa === false && !row.mfa_required && row.email !== null,
              onChon: () => thaoTac.moBatBuocMfa(row),
            },
            {
              khoa: 'bo-mfa',
              nhan: t('nguoiDung.mfa.boBatBuocMuc'),
              icon: <StopOutlined />,
              hien: toChucBatBuocMfa === false && row.mfa_required,
              voHieu: thaoTac.dangChayVoi(row.user_id),
              onChon: () => thaoTac.boBatBuocMfaNgay(row),
            },
            {
              khoa: 'gui-lai',
              nhan: t('nguoiDung.guiLaiLoiMoi'),
              icon: <MailOutlined />,
              hien: guiLaiLoiMoiDuoc(row.status),
              voHieu: thaoTac.dangChayVoi(row.user_id),
              onChon: () => thaoTac.guiLaiLoiMoi(row),
            },
            {
              khoa: 'doi-email-nhan-thu',
              nhan: t('nguoiDung.doiEmailNhanThu'),
              icon: <InboxOutlined />,
              // Không tự đổi của chính mình (ADR-0101, backend trả 403); bản ghi mồ côi không có tài khoản.
              hien: doiEmailNhanThuDuoc(row, toiLa),
              onChon: () => thaoTac.moDoiEmailNhanThu(row),
            },
            {
              khoa: 'dat-lai-mat-khau',
              nhan: t('nguoiDung.layLai.guiDatLaiMatKhau'),
              icon: <KeyOutlined />,
              // Chính mình đổi mật khẩu ở Tài khoản của tôi.
              hien: guiDatLaiMatKhauDuoc(row, toiLa),
              onChon: () => thaoTac.moDatLaiMatKhau(row),
            },
            {
              khoa: 'mo-khoa-dang-nhap',
              nhan: t('nguoiDung.layLai.moKhoaDangNhap'),
              moTa: t('nguoiDung.layLai.moKhoaDangNhapMo'),
              icon: <UnlockOutlined />,
              hien: dangTamKhoaDangNhap(row),
              voHieu: thaoTac.dangChayVoi(row.user_id),
              onChon: () => thaoTac.moKhoaDangNhapNgay(row),
            },
            {
              khoa: 'mo-khoa',
              nhan: t('nguoiDung.moKhoaTaiKhoan'),
              icon: <UnlockOutlined />,
              hien: moKhoaDuoc(row.status),
              voHieu: thaoTac.dangChayVoi(row.user_id),
              onChon: () => thaoTac.moKhoaNgay(row),
            },
            {
              khoa: 'dat-lai-mfa',
              nhan: t('nguoiDung.layLai.datLaiMfa'),
              icon: <RedoOutlined />,
              nhom: 'pha-huy',
              // Chính mình gỡ ở Tài khoản của tôi (backend trả 403).
              hien: datLaiMfaDuoc(row, toiLa),
              onChon: () => thaoTac.moDatLaiMfa(row, toChucBatBuocMfa === true || row.mfa_required),
            },
            {
              khoa: 'khoa',
              nhan: t('nguoiDung.khoaTaiKhoan'),
              icon: <LockOutlined />,
              nhom: 'pha-huy',
              // Không tự khoá mình: backend cũng từ chối (409).
              hien: khoaDuoc(row.status) && row.user_id !== toiLa,
              onChon: () => thaoTac.moKhoa(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.cauHinhHeThong') },
            { label: t('nav.muc.cauHinhHeThong') },
            { label: tieuDe },
          ]}
          titleIcon={<TeamOutlined className="text-primary-600" />}
          title={tieuDe}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void q.refetch()}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              <Button variant="primary" size="md" icon={<UserAddOutlined />} onClick={thaoTac.moMoi}>
                {t('nguoiDung.moiNguoiDung')}
              </Button>
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Select<UserStatus>
            allowClear
            placeholder={t('nguoiDung.tatCaTrangThai')}
            aria-label={t('chung.trangThai')}
            className="min-w-[11rem]"
            options={THU_TU_TRANG_THAI.map((s) => ({
              value: s,
              label: t(TRANG_THAI_NGUOI_DUNG[s].khoa),
            }))}
            value={trangThai}
            onChange={setTrangThai}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {loiTaiDau ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">
                {t('nguoiDung.loiDanhSach', { loi: dichLoi(q.error) })}
              </span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <>
              <DataTable<UserProfile>
                caption={t('nguoiDung.bangCaption')}
                rowKey="user_id"
                dataSource={q.dong}
                columns={columns}
                loading={dangTaiDau}
                size="middle"
                pagination={false}
                locale={{
                  emptyText: dangTaiDau ? (
                    ' '
                  ) : (
                    <Empty
                      icon={TeamOutlined}
                      // Rỗng vì bộ lọc: bỏ mô tả và lời mời hành động.
                      title={t(coLoc ? 'nguoiDung.rongLoc' : 'nguoiDung.rongThat')}
                      description={coLoc ? undefined : t('nguoiDung.rongThatMo')}
                      action={
                        coLoc ? undefined : (
                          <Button
                            variant="primary"
                            size="md"
                            icon={<UserAddOutlined />}
                            onClick={thaoTac.moMoi}
                          >
                            {t('nguoiDung.moiNguoiDauTien')}
                          </Button>
                        )
                      }
                    />
                  ),
                }}
              />
              {q.dong.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted">
                    {t(q.hasNextPage ? 'nguoiDung.dangHienCoThem' : 'nguoiDung.dangHien', {
                      so: soVN(q.dong.length, locale),
                    })}
                  </span>
                  {q.isFetchNextPageError && (
                    <span role="alert" className="min-w-0 flex-1 text-xs text-danger-700">
                      {t('nguoiDung.loiTaiThem', { loi: dichLoi(q.error) })}
                    </span>
                  )}
                  {q.hasNextPage && (
                    <Button
                      variant="outline"
                      size="sm"
                      loading={q.isFetchingNextPage}
                      onClick={() => void q.fetchNextPage()}
                    >
                      {q.isFetchNextPageError ? t('chung.thuLai') : t('nguoiDung.taiThem')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {thaoTac.hopThoai}
    </div>
  );
}
